/**
 * 딜 보고서 (v2, FR-E1) — internal A4 print report: overview / progress /
 * documents / gate summary / regulatory profile status / risks & unconfirmed.
 * Report is itself an issued document (immutable snapshot, FR-E2 / DP-5).
 */

import type {
  ChecklistItem,
  DocumentRecord,
  GateResult,
  PaymentTerm,
  RuleContext,
  ShipperProfile,
  WorkflowStage,
} from "@/types";
import { dash, esc, kvTable, printPage } from "./html";

const SEV_BADGE: Record<string, string> = {
  BLOCK: "block",
  ERROR: "warn",
  WARN: "warn",
  INFO: "ok",
};

export interface DealReportInput {
  ctx: RuleContext;
  shipper: ShipperProfile | null;
  paymentTerm?: PaymentTerm | null;
  gateResult: GateResult | null;
  stages: WorkflowStage[];
  documents: DocumentRecord[];
  checklist: ChecklistItem[];
  issuedAt: string;
}

export function renderDealReport(input: DealReportInput): string {
  const { ctx, shipper, paymentTerm, gateResult, stages, documents, checklist, issuedAt } = input;
  const { deal, country } = ctx;
  const lines = ctx.lines.filter((l) => l.item_kind === "product");
  const totalAmount =
    Math.round(lines.reduce((s, l) => s + l.quantity * l.unit_price, 0) * 100) / 100;

  // ① 개요
  const overview = kvTable([
    ["딜 번호 / 제목", `${deal.deal_no} · ${deal.title ?? ""}`],
    ["목적국", `${country.name_ko ?? country.name_en} (${deal.destination_country})`],
    ["바이어 / 수하인", `${ctx.partners.buyer?.company_name ?? "—"} / ${ctx.partners.consignee?.company_name ?? "—"}`],
    ["인코텀즈", `${deal.incoterm_id ?? "—"} ${deal.incoterm_named_place ?? ""} (Incoterms® ${deal.incoterm_version})`],
    ["결제조건", paymentTerm?.label ?? "—"],
    ["총 금액", `${totalAmount} ${deal.currency}`],
    ["수출자", shipper?.company_name ?? "—"],
  ]);

  // ② 진행 (stage map vs current status)
  const curIdx = stages.findIndex((s) => s.deal_status === deal.status);
  const progress = `<table class="cols"><thead><tr><th>#</th><th>단계</th><th>담당</th><th>상태</th></tr></thead><tbody>${stages
    .map((s, i) => {
      const state =
        curIdx < 0 ? "—" : i < curIdx ? "완료 추정" : i === curIdx ? "◀ 현재" : "예정";
      return `<tr><td>${Math.round(s.sort / 10)}</td><td>${esc(s.label_ko)}</td><td>${esc(s.owner)}</td><td>${state}</td></tr>`;
    })
    .join("")}</tbody></table>`;

  // ③ 서류 현황
  const docsTable = `<table class="cols"><thead><tr><th>서류</th><th>번호</th><th>버전</th><th>상태</th></tr></thead><tbody>${
    documents.length
      ? documents
          .map(
            (d) =>
              `<tr><td>${esc(d.doc_type)}</td><td>${dash(d.doc_no)}</td><td>v${d.current_version}</td><td>${esc(d.status)}</td></tr>`,
          )
          .join("")
      : `<tr><td colspan="4">발행된 서류 없음</td></tr>`
  }</tbody></table>
${kvTable([
    ["수출신고필증 번호", deal.export_declaration_no],
    ["B/L · AWB 번호", deal.transport_doc_no],
    ["적하보험 증권번호", deal.insurance_policy_no],
  ])}`;

  // ④ 게이트 요약
  const gate = gateResult
    ? `<p>
<span class="badge block">BLOCK ${gateResult.blocks}</span>
<span class="badge warn">ERROR ${gateResult.errors}</span>
<span class="badge warn">WARN ${gateResult.warns}</span>
<span class="badge ok">INFO ${gateResult.infos}</span>
— 게이트 ${gateResult.passed ? '<span class="badge ok">통과</span>' : '<span class="badge block">미통과</span>'}</p>
<table class="cols"><thead><tr><th>심각도</th><th>룰</th><th>내용</th><th>상태</th></tr></thead><tbody>${
        gateResult.findings.length
          ? gateResult.findings
              .map(
                (f) =>
                  `<tr><td><span class="badge ${SEV_BADGE[f.severity] ?? ""}">${f.severity}</span></td><td><code>${esc(f.rule_id)}</code></td><td>${esc(f.message)}</td><td>${esc(f.status)}</td></tr>`,
              )
              .join("")
          : `<tr><td colspan="4">지적 사항 없음</td></tr>`
      }</tbody></table>`
    : "<p>게이트 미평가</p>";

  // ⑤ 규제 체크 현황 (checklist)
  const doneCount = checklist.filter((c) => c.status === "done").length;
  const regStatus = `<p>체크리스트 ${doneCount}/${checklist.length} 완료</p>
<ul class="check">${checklist
    .map((c) => `<li class="${c.status === "done" ? "done" : ""}">${esc(c.label)}</li>`)
    .join("")}</ul>`;

  // ⑥ 리스크·미확인
  const risks: string[] = [];
  if (ctx.products.some((p) => !p.hs_verified))
    risks.push("HS 세번 통관사 미확정 — 관세·규제 분류의 최우선 선결 항목");
  for (const f of gateResult?.findings ?? []) {
    if (f.severity === "BLOCK" || (f.severity === "WARN" && f.status === "open"))
      risks.push(`[${f.severity}] ${f.message}`);
  }
  if (country.tariff_note) risks.push(`관세: ${country.tariff_note}`);
  const riskList = risks.length
    ? `<ul>${risks.map((r) => `<li>${esc(r)}</li>`).join("")}</ul>`
    : "<p>기록된 리스크 없음</p>";

  const body = `
<h1>딜 보고서 — ${esc(deal.deal_no)}</h1>
<p class="meta">작성일 ${esc(issuedAt)} · export-desk 자동 생성 · 내부 보고용</p>
<h2>1. 개요</h2>${overview}
<h2>2. 진행 단계</h2>${progress}
<h2>3. 서류 현황</h2>${docsTable}
<h2>4. 발행 게이트 결과</h2>${gate}
<h2>5. 규제·체크리스트 현황</h2>${regStatus}
<h2>6. 리스크 · 미확인 항목</h2>${riskList}`;

  return printPage(`딜 보고서 — ${deal.deal_no}`, body);
}
